import {
  PlanningActionMetadataMoveOrdersToRoutesDtoModelType,
  PlanningActionMetadataOptimizeOrdersToTargetRoutesDtoModelType,
  PlanningActionsStatus,
} from '../../../models';
import * as OrdersToTargetRoutesNotifications from '../../../../../notifications/scheduling/ordersToTargetRoutes';
import * as ScheduleNotifications from '../../../../../notifications/scheduling/schedule';
import getAppEnv from '../../../utils/getAppEnv';
import getAppRoot from '../../../utils/getAppRoot';
import { TopMenuView } from '../../../../../components/scheduling/ViewMenu';

type AppStore = ReturnType<typeof getAppRoot>;
type AppEnv = ReturnType<typeof getAppEnv>;

type SchedulingPlanningAction =
  | PlanningActionMetadataMoveOrdersToRoutesDtoModelType
  | PlanningActionMetadataOptimizeOrdersToTargetRoutesDtoModelType;

const handleFinishedStatus = async (
  appStore: AppStore,
  appEnv: AppEnv,
  fullPlanningAction: SchedulingPlanningAction,
  selectedDate: string | undefined,
  projectId: string,
) => {
  const { model, ui } = appStore;

  if (fullPlanningAction.isUndo) {
    return;
  }

  await model.fetchProjectRoutesExtraData(projectId, ui.selectedDate);

  const isScheduleAll = appStore.planningActions.scheduleAllForActionUuid(fullPlanningAction.actionUuid);
  if (isScheduleAll) {
    ui.setCurrentView(TopMenuView.SCHEDULED);
  }

  // Actions that scaffold or rewrite routes need a full routes fetch on completion. The per-route
  // OPTIMIZED subscription only carries a light payload (no solution/timeline).
  const fetchForActionUuid = appStore.planningActions.fetchForActionUuid(fullPlanningAction.actionUuid);

  if (isScheduleAll || fetchForActionUuid) {
    await model.fetchProjectRoutes(projectId, ui.selectedDate, { background: true });
  }

  appEnv.notification(OrdersToTargetRoutesNotifications.buildMoveOrdersToRoutesSuccessNotification(
    fullPlanningAction.actionUuid,
    fullPlanningAction.output,
  ));

  // Re-fit the map to the day's routes once the schedule has finished and the
  // page is still showing that day. This covers the orders-page flow, where the
  // redirect to the routes page fits the map *before* the backend finishes
  // building the routes — without this the viewport never moves to the newly
  // scheduled routes. Guarded on the date so we don't yank the camera if the
  // user has since navigated to a different day.
  //
  // A fresh optimization (schedule all, or routes scaffolded from templates)
  // always fits the day. Anything else (a stop move or a drag onto an existing
  // route) only refits when the day's orders are off screen.
  // fetchForActionUuid can't stand in for this: it is set for every
  // re-optimize, including map drags.
  if (selectedDate === fullPlanningAction.startDate) {
    const isScaffold = appStore.planningActions.scaffoldRoutesForActionUuid(fullPlanningAction.actionUuid);
    appStore.planningActions.setMapBoundsBasedOnAction(fullPlanningAction, { forceFit: isScheduleAll || isScaffold });
  }
};

export default (fullPlanningAction: SchedulingPlanningAction) => async () => {
  const { store } = fullPlanningAction;
  const appStore = getAppRoot(store);
  const appEnv = getAppEnv(store);
  const { ui } = appStore;
  const maybeProjectId = ui.selectedWorkspace?.id;
  const { selectedDate } = ui;

  const isTerminalStatus = fullPlanningAction.status === PlanningActionsStatus.FINISHED
    || fullPlanningAction.status === PlanningActionsStatus.FAILED
    || fullPlanningAction.status === PlanningActionsStatus.FINISHED_WITH_FAILED;

  try {
    if (!maybeProjectId || maybeProjectId !== String(fullPlanningAction.projectId)) {
      return;
    }

    // Type narrowing: projectId is guaranteed to be defined after the guard above
    const projectId: string = maybeProjectId;

    switch (fullPlanningAction.status) {
      case PlanningActionsStatus.NOT_STARTED:
      case PlanningActionsStatus.IN_PROGRESS: {
        ui.setIsOptimizing(true);
        ui.setSchedulingError(null);
        break;
      }
      case PlanningActionsStatus.FAILED:
      case PlanningActionsStatus.FINISHED_WITH_FAILED: {
        ui.setIsOptimizing(false);
        // Persist the reason so the Unscheduled panel can show it after the toast is gone. A failed
        // UNDO is not a scheduling failure, so it must not set the (mis-framed) scheduling banner.
        if (!fullPlanningAction.isUndo) {
          ui.setSchedulingError(ScheduleNotifications.buildOutputError(fullPlanningAction.output));
        }
        appEnv.notification(ScheduleNotifications.scheduleFailureNotification(
          projectId,
          fullPlanningAction.output,
        ));
        break;
      }
      case PlanningActionsStatus.FINISHED: {
        ui.setIsOptimizing(false);
        ui.setSchedulingError(null);
        await handleFinishedStatus(appStore, appEnv, fullPlanningAction, selectedDate, projectId);
        break;
      }
      default:
    }

    // Always query for lastUndoable and nextRedoable actions
    appStore.planningActions.getUndoableRedoableActions(true);
  } finally {
    if (isTerminalStatus) {
      appStore.planningActions.clearFetchAllRoutesAction(fullPlanningAction.actionUuid);
      appStore.planningActions.clearScheduleAllAction(fullPlanningAction.actionUuid);
      appStore.planningActions.clearScaffoldRoutesAction(fullPlanningAction.actionUuid);
    }
  }
};
